import { isMacHost } from "~/lib/platform";

export type ShortcutSection = "navigation" | "messaging" | "formatting" | "search" | "misc";

export interface Shortcut {
  id: string;
  keys: string[];
  description: string;
  section: ShortcutSection;
  alternateKeys?: string[][];
  platformKeys?: {
    mac: string[];
    other: string[];
  };
  platformAlternateKeys?: {
    mac: string[][];
    other: string[][];
  };
}

export const SHORTCUT_SECTIONS: Array<{ id: ShortcutSection; label: string }> = [
  { id: "navigation", label: "Navigation" },
  { id: "messaging", label: "Messaging" },
  { id: "formatting", label: "Formatting" },
  { id: "search", label: "Search" },
  { id: "misc", label: "Misc" },
];

export const SHORTCUTS: Shortcut[] = [
  {
    id: "global-search",
    keys: ["Mod", "K"],
    description: "Global search",
    section: "navigation",
  },
  {
    id: "close-popovers",
    keys: ["Esc"],
    description: "Close popovers",
    section: "navigation",
  },
  {
    id: "send-message",
    keys: ["Enter"],
    description: "Send message",
    section: "messaging",
  },
  {
    id: "new-line",
    keys: ["Shift", "Enter"],
    description: "New line",
    section: "messaging",
  },
  {
    id: "bold",
    keys: ["Mod", "B"],
    description: "Bold",
    section: "formatting",
  },
  {
    id: "italic",
    keys: ["Mod", "I"],
    description: "Italic",
    section: "formatting",
  },
  {
    id: "code",
    keys: ["Mod", "E"],
    description: "Code",
    section: "formatting",
  },
  {
    id: "quote",
    keys: ["Mod", "Shift", "."],
    description: "Quote",
    section: "formatting",
  },
  {
    id: "slash-command-palette",
    keys: ["Mod", "."],
    description: "Slash command palette",
    section: "formatting",
  },
  {
    id: "search-next-result",
    keys: ["↓"],
    description: "Next result",
    section: "search",
  },
  {
    id: "search-previous-result",
    keys: ["↑"],
    description: "Previous result",
    section: "search",
  },
  {
    id: "search-open-result",
    keys: ["Enter"],
    description: "Open result",
    section: "search",
  },
  {
    id: "shortcut-help",
    keys: ["Mod", "/"],
    description: "Keyboard shortcuts",
    section: "misc",
  },
];

export function isMacPlatform(platformLabel?: string): boolean {
  return isMacHost(platformLabel);
}

export function formatShortcutKeyParts(shortcut: Shortcut, isMac: boolean): string[][] {
  const keys = shortcut.platformKeys
    ? isMac
      ? shortcut.platformKeys.mac
      : shortcut.platformKeys.other
    : shortcut.keys;
  const alternateKeys = shortcut.platformAlternateKeys
    ? isMac
      ? shortcut.platformAlternateKeys.mac
      : shortcut.platformAlternateKeys.other
    : (shortcut.alternateKeys ?? []);
  return [keys, ...alternateKeys].map((combo) => combo.map((key) => formatKey(key, isMac)));
}

export function formatShortcutKeys(shortcut: Shortcut, isMac: boolean): string {
  return formatShortcutKeyParts(shortcut, isMac)
    .map((combo) => combo.join("+"))
    .join(" / ");
}

export function globalSearchHintLabel(): string {
  const shortcut = SHORTCUTS.find((entry) => entry.id === "global-search");
  const isMac = isMacHost();
  const combo = shortcut ? formatShortcutKeyParts(shortcut, isMac)[0] : undefined;
  return combo ? combo.join(isMac ? "" : " ") : "";
}

function formatKey(key: string, isMac: boolean): string {
  if (key === "Mod") return isMac ? "⌘" : "Ctrl";
  if (key === "Shift") return isMac ? "⇧" : "Shift";
  if (key === "Alt") return isMac ? "⌥" : "Alt";
  return key;
}
